import { translate } from '@thoughtspot/i18n';
import { OptionsGroup } from '@thoughtspot/radiant-react/widgets/select/base-select';
import _ from 'lodash';
import {
    DateFilterType,
    FilterTypes,
} from '/@services/generated/graphql-types';

export enum FixedDateFilterOperators {
    BETWEEN = 'is_between',
    NOT_BETWEEN = 'is_not_in_between',
    ON = 'is_on',
    NOT_ON = 'is_not_on',
    AFTER = 'is_after',
    ON_OR_AFTER = 'is_on_or_after',
    BEFORE = 'is_before',
    ON_OR_BEFORE = 'is_on_or_before',
    MONTH_AND_YEAR = 'is_month_and_year',
    QUARTER_AND_YEAR = 'is_quarter_and_year',
    YEAR = 'is_year',
}

export const operatorToDateTypeAndFilterType: {
    [key in FixedDateFilterOperators]: {
        filterType: FilterTypes;
        dateFilterType: DateFilterType;
        isNegate: boolean;
    };
} = {
    [FixedDateFilterOperators.BETWEEN]: {
        filterType: FilterTypes.BwInc,
        dateFilterType: DateFilterType.ExactDateRange,
        isNegate: false,
    },
    [FixedDateFilterOperators.NOT_BETWEEN]: {
        filterType: FilterTypes.BwInc,
        dateFilterType: DateFilterType.ExactDateRange,
        isNegate: true,
    },
    [FixedDateFilterOperators.ON]: {
        filterType: FilterTypes.Eq,
        dateFilterType: DateFilterType.ExactDate,
        isNegate: false,
    },
    [FixedDateFilterOperators.NOT_ON]: {
        filterType: FilterTypes.Ne,
        dateFilterType: DateFilterType.ExactDate,
        isNegate: false,
    },
    [FixedDateFilterOperators.AFTER]: {
        filterType: FilterTypes.Gt,
        dateFilterType: DateFilterType.ExactDate,
        isNegate: false,
    },
    [FixedDateFilterOperators.ON_OR_AFTER]: {
        filterType: FilterTypes.Ge,
        dateFilterType: DateFilterType.ExactDate,
        isNegate: false,
    },
    [FixedDateFilterOperators.ON_OR_BEFORE]: {
        filterType: FilterTypes.Le,
        dateFilterType: DateFilterType.ExactDate,
        isNegate: false,
    },
    [FixedDateFilterOperators.BEFORE]: {
        filterType: FilterTypes.Lt,
        dateFilterType: DateFilterType.ExactDate,
        isNegate: false,
    },
    [FixedDateFilterOperators.MONTH_AND_YEAR]: {
        filterType: FilterTypes.Eq,
        dateFilterType: DateFilterType.MonthYear,
        isNegate: false,
    },
    [FixedDateFilterOperators.QUARTER_AND_YEAR]: {
        filterType: FilterTypes.Eq,
        dateFilterType: DateFilterType.QuarterYear,
        isNegate: false,
    },
    [FixedDateFilterOperators.YEAR]: {
        filterType: FilterTypes.Eq,
        dateFilterType: DateFilterType.YearOnly,
        isNegate: false,
    },
};

export const getFixedDateFilterOperatorOptions = (): OptionsGroup[] => [
    {
        title: '',
        options: [
            {
                id: FixedDateFilterOperators.BETWEEN,
                label: translate(
                    `dateFilter.${FixedDateFilterOperators.BETWEEN}`,
                ),
            },
            {
                id: FixedDateFilterOperators.NOT_BETWEEN,
                label: translate(
                    `dateFilter.${FixedDateFilterOperators.NOT_BETWEEN}`,
                ),
            },
        ],
    },
    {
        title: '',
        options: [
            {
                id: FixedDateFilterOperators.ON,
                label: translate(`dateFilter.${FixedDateFilterOperators.ON}`),
            },
            {
                id: FixedDateFilterOperators.NOT_ON,
                label: translate(
                    `dateFilter.${FixedDateFilterOperators.NOT_ON}`,
                ),
            },
        ],
    },
    {
        title: '',
        options: [
            {
                id: FixedDateFilterOperators.AFTER,
                label: translate(
                    `dateFilter.${FixedDateFilterOperators.AFTER}`,
                ),
            },
            {
                id: FixedDateFilterOperators.ON_OR_AFTER,
                label: translate(
                    `dateFilter.${FixedDateFilterOperators.ON_OR_AFTER}`,
                ),
            },
            {
                id: FixedDateFilterOperators.BEFORE,
                label: translate(
                    `dateFilter.${FixedDateFilterOperators.BEFORE}`,
                ),
            },
            {
                id: FixedDateFilterOperators.ON_OR_BEFORE,
                label: translate(
                    `dateFilter.${FixedDateFilterOperators.ON_OR_BEFORE}`,
                ),
            },
        ],
    },
    {
        title: '',
        options: [
            {
                id: FixedDateFilterOperators.MONTH_AND_YEAR,
                label: translate(
                    `dateFilter.${FixedDateFilterOperators.MONTH_AND_YEAR}`,
                ),
            },
            {
                id: FixedDateFilterOperators.QUARTER_AND_YEAR,
                label: translate(
                    `dateFilter.${FixedDateFilterOperators.QUARTER_AND_YEAR}`,
                ),
            },
            {
                id: FixedDateFilterOperators.YEAR,
                label: translate(`dateFilter.${FixedDateFilterOperators.YEAR}`),
            },
        ],
    },
];

export const getFixedDateFilterOperator = (
    filterType: FilterTypes,
    dateFilterType: DateFilterType,
    isNegate = false,
): FixedDateFilterOperators | null => {
    if (_.isEqual(dateFilterType, DateFilterType.ExactDate)) {
        switch (filterType) {
            case FilterTypes.Eq:
                return FixedDateFilterOperators.ON;
            case FilterTypes.Ne:
                return FixedDateFilterOperators.NOT_ON;
            case FilterTypes.Lt:
                return FixedDateFilterOperators.BEFORE;
            case FilterTypes.Le:
                return FixedDateFilterOperators.ON_OR_BEFORE;
            case FilterTypes.Gt:
                return FixedDateFilterOperators.AFTER;
            case FilterTypes.Ge:
                return FixedDateFilterOperators.ON_OR_AFTER;
            default:
                return FixedDateFilterOperators.ON;
        }
    }
    if (_.isEqual(dateFilterType, DateFilterType.ExactDateRange)) {
        return isNegate
            ? FixedDateFilterOperators.NOT_BETWEEN
            : FixedDateFilterOperators.BETWEEN;
    }
    if (_.isEqual(dateFilterType, DateFilterType.MonthYear)) {
        return FixedDateFilterOperators.MONTH_AND_YEAR;
    }
    if (_.isEqual(dateFilterType, DateFilterType.QuarterYear)) {
        return FixedDateFilterOperators.QUARTER_AND_YEAR;
    }
    if (_.isEqual(dateFilterType, DateFilterType.YearOnly)) {
        return FixedDateFilterOperators.YEAR;
    }
    return FixedDateFilterOperators.BETWEEN;
};
